import { useState, useCallback } from "react";
import { literals } from "@/constants/literals";
import { GARAGE_STEPS } from "@/constants/selectsLists";
import useGarageStore from "@/store/useGarbage.store";
import { useNavigate } from "react-router-dom";
import OrderHeader from "../components/OrderHeader";
import {
  askOrderExist,
  getOrderDataBySapOrderNumber,
} from "@/lib/services/order";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/store/useLoader";
import type { IGarage, ExitGarageData } from "@/types/types.d";
import ExitGarageInputsl from "./components/ExitGarageInputs";
import CreateGarageButtons from "./components/CreateGarageButtons";
import CommonGaragesInputs from "./components/CommonGaragesInputs";
import ConfirmContinueGarageModal from "./components/ConfirmContinueGarageModal";

const initial_status = {
  garage: "",
  step: "",
  orderNumber: "",
};

const CreateGarage = () => {
  const [commonGarageData, setCommonGarageData] =
    useState<IGarage>(initial_status);
  const [exitGarageData, setExitGarageData] = useState<ExitGarageData | null>(
    null,
  );
  const { updategarageData } = useGarageStore();
  const navigate = useNavigate();
  const { upLoader, downLoader } = useLoader();
  const [needChangeData, setNeedChangeData] = useState(false);
  const [exitGarageForm, setExitGarageForm] = useState(false);
  const [used9R1, setUsed9R1] = useState(false);
  const [order9r1Message, setOrder9r1Message] = useState<string>("");

  const changeCommonGarageData = <KeyName extends keyof IGarage>(
    id: KeyName,
    value: IGarage[KeyName],
  ) => {
    if (needChangeData) setNeedChangeData(false);
    setCommonGarageData((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const changeExitGarageData = <Key extends keyof ExitGarageData>(
    id: Key,
    value: ExitGarageData[Key],
  ) => {
    if (used9R1) setUsed9R1(false);

    setExitGarageData((prevState) => {
      const newState = prevState
        ? {
            ...prevState,
            [id]: value,
          }
        : {
            [id]: value,
          };
      if (
        id === "originReturn" &&
        value === true &&
        prevState &&
        prevState.document9R1 &&
        prevState.document9R1.length > 0
      ) {
        newState.document9R1 = "";
      }
      return newState;
    });
  };

  const noValidExitGarageData = () => {
    const { orderOut, serialOut, document9R1, originReturn } =
      exitGarageData || {};
    return !orderOut ||
      orderOut.length < 1 ||
      !serialOut ||
      serialOut.length < 1 ||
      (!originReturn && (!document9R1 || document9R1.length !== 10)) ||
      (originReturn === true && document9R1) ||
      used9R1
      ? true
      : false;
  };

  const isValid = () =>
    !needChangeData &&
    commonGarageData.garage &&
    commonGarageData.orderNumber &&
    commonGarageData.orderNumber.length >= 10 &&
    commonGarageData.orderNumber.length <= 12 &&
    commonGarageData.step &&
    (!exitGarageForm || !noValidExitGarageData())
      ? true
      : false;

  const confirmCanContinue = useCallback(async (): Promise<false | number> => {
    const { orderNumber: sapWorkOrder, garage: area, step } = commonGarageData;
    let params = {
      sapWorkOrder,
      area,
      step,
    };
    if (exitGarageData !== null) {
      type ExitParams = {
        orderOut?: string;
        serialOut?: string;
        originReturn?: boolean;
        document9r1?: string;
      };
      const { document9R1, ...restExitGarageData } = exitGarageData;
      const exitParams: ExitParams = { ...restExitGarageData };
      if (document9R1) {
        exitParams.document9r1 = document9R1;
      }
      params = { ...params, ...exitParams };
    }
    try {
      upLoader();
      const orderInfo = await askOrderExist(params);
      const { restrict, message, existing, area, step, id, warning } =
        orderInfo;
      downLoader();
      if (restrict && !exitGarageForm) {
        setNeedChangeData(true);
        appToast.warning(literals.noValidData, message);
        return false;
      }
      if (restrict && exitGarageForm) {
        setUsed9R1(true);
        appToast.warning(literals.noValidData, message);
        return false;
      }
      if (warning) {
        setOrder9r1Message(message);
        return false;
      }
      if (existing && id && area === "TGQEC000" && step === "Findings") {
        return id;
      }
      return -1;
    } catch (error) {
      downLoader();
      if (error) {
        appToast.error(literals.aProblem, literals.getNecesaryInfoError);
        return false;
      }
      return false;
    }
  }, [downLoader, upLoader, commonGarageData, exitGarageData, exitGarageForm]);

  const handleOnSubmit = async (
    e: React.BaseSyntheticEvent<
      Event,
      EventTarget & HTMLFormElement,
      EventTarget
    >,
  ) => {
    e.preventDefault();
    if (!isValid()) return;
    if (commonGarageData.step === literals.garageExit && !exitGarageForm) {
      switchFormStep();
      return;
    }
    const canContinue =
      order9r1Message.length > 0 ? -1 : await confirmCanContinue();
    if (!canContinue) return;
    const step = GARAGE_STEPS.find(
      (step) => step.value === commonGarageData.step,
    )!;
    let newGarageData = { ...commonGarageData, step: step.label };
    if (exitGarageForm && exitGarageData) {
      newGarageData = { ...newGarageData, ...exitGarageData };
    }
    if (canContinue !== -1) {
      newGarageData.orderId = canContinue;
    }
    updategarageData(newGarageData);
    navigate("new-group");
  };

  const searchInData = async () => {
    try {
      upLoader();
      const askedData = await getOrderDataBySapOrderNumber(
        commonGarageData.orderNumber,
      );
      const dataIn =
        askedData.length > 0
          ? askedData[0]
          : { orderIn: undefined, serialIn: undefined };
      if (dataIn.orderIn) changeExitGarageData("orderOut", dataIn.orderIn);
      if (dataIn.serialIn) changeExitGarageData("serialOut", dataIn.serialIn);

      downLoader();
    } catch (error) {
      if (
        error === literals.errorNotFound ||
        (error instanceof Error && error.message === literals.notFound)
      ) {
        downLoader();
        return;
      } else {
        appToast.error(literals.aProblem, literals.getInfoError);
      }
      downLoader();
    }
  };

  const switchFormStep = async () => {
    if (exitGarageForm) {
      if (exitGarageData !== null) setExitGarageData(null);
      setExitGarageForm(false);
      return;
    }
    if (!isValid()) return;
    searchInData();
    setExitGarageData((prevState) => {
      const newState = {
        ...prevState,
        originReturn: false,
      };
      return newState;
    });
    setExitGarageForm(true);
  };

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <OrderHeader />
      <form
        onSubmit={handleOnSubmit}
        className="flex flex-col flex-1 justify-between md:justify-start"
      >
        <div className="w-full px-4 md:px-14 lg:px-[calc((100%-797px)/2)] pt-4 md:flex md:flex-row md:gap-x-8 flex-wrap">
          {!exitGarageForm ? (
            <CommonGaragesInputs
              commonGarageData={commonGarageData}
              changeCommonGarageData={changeCommonGarageData}
            />
          ) : (
            <ExitGarageInputsl
              exitGarageData={exitGarageData}
              changeExitGarageData={changeExitGarageData}
            />
          )}
        </div>
        <CreateGarageButtons
          isExitGarageForm={exitGarageForm}
          continueCapture={handleOnSubmit}
          switchFormStep={switchFormStep}
          isValidData={isValid()}
        />
        <ConfirmContinueGarageModal
          messageToShow={order9r1Message}
          closeModal={() => setOrder9r1Message("")}
          continueCapture={handleOnSubmit}
        />
      </form>
    </div>
  );
};

export default CreateGarage;
